import { RedoSideNavigation } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { filterTruthy } from "@redotech/util/array";
import { memo, useContext, useMemo } from "react";
import { NavigationVersionContext } from "../navigation-version-context";
import { useAeoNavigation } from "./aeo/aeo-navigation";
import { useConversionNavigation } from "./conversion/conversion-navigation";
import { useCustomersNavigation } from "./customers/customers-navigation";
import { useFinanceNavigation } from "./finance/finance-navigation";
import { useHomeNavigation } from "./home/home-navigation";
import { useInventoryNavigation } from "./inventory/inventory-navigation";
import { useMarketingNavigation } from "./marketing/marketing-navigation";
import { useOrdersNavigation } from "./orders/orders-navigation";
import { useOrdersPrimaryNavigation } from "./orders/orders-primary-navigation";
import { useRedoSuppliesNavigation } from "./redo-supplies/redo-supplies-navigation";
import { useReturnsNavigation } from "./returns/returns-navigation";
import { useSettingsItems } from "./settings";
import { useShippingNavigation } from "./shipping/shipping-navigation";
import { useShopperAgentNavigation } from "./shopper-agent/shopper-agent-navigation";
import { useSupportNavigation } from "./support/support-navigation";

export const MerchantAppSideNav = memo(function MerchantAppSideNav({
  enterSettingsUrl,
  exitSettingsUrl,
  settingsRoot,
  hideNav,
  hideTeamSwitcher = false,
}: {
  enterSettingsUrl: string;
  exitSettingsUrl: string;
  settingsRoot: string;
  hideNav: boolean;
  hideTeamSwitcher?: boolean;
}) {
  const { newNavigationEnabled } = useContext(NavigationVersionContext);

  const homeEntry = useHomeNavigation();
  const aeoEntry = useAeoNavigation();
  const conversionEntry = useConversionNavigation();
  const marketing = useMarketingNavigation();
  const shopperAgent = useShopperAgentNavigation();
  const support = useSupportNavigation();
  const customers = useCustomersNavigation();
  const finance = useFinanceNavigation();
  const redoSupplies = useRedoSuppliesNavigation();

  // New navigation: separate Shipping and Returns sections + standalone Orders tab
  const orders = useOrdersPrimaryNavigation();
  const shipping = useShippingNavigation();
  const returns = useReturnsNavigation();

  // Old navigation: combined Operations section
  const ordersLegacy = useOrdersNavigation();

  // Inventory management is a primary-level product in both layouts.
  const inventory = useInventoryNavigation();

  const allPageEntries = useMemo(() => {
    if (newNavigationEnabled) {
      // `orders` must come before `shipping`: its pathRoot
      // (`/shipping/outbound-labels/orders`) is a strict prefix of the
      // shipping pathRoot (`/shipping`), so the selectedPrimaryTab
      // `find()` logic would otherwise highlight Shipping when the
      // user is on an orders page.
      return filterTruthy([
        homeEntry,
        aeoEntry,
        conversionEntry,
        marketing,
        shopperAgent,
        orders,
        shipping,
        returns,
        inventory,
        support,
        customers,
        finance,
        redoSupplies,
      ]);
    }

    return filterTruthy([
      homeEntry,
      aeoEntry,
      conversionEntry,
      marketing,
      shopperAgent,
      ordersLegacy,
      inventory,
      support,
      customers,
      finance,
      redoSupplies,
    ]);
  }, [
    newNavigationEnabled,
    homeEntry,
    aeoEntry,
    conversionEntry,
    marketing,
    shopperAgent,
    orders,
    shipping,
    returns,
    ordersLegacy,
    inventory,
    support,
    customers,
    finance,
    redoSupplies,
  ]);

  const settingsItems = useSettingsItems();

  return (
    <RedoSideNavigation
      enterSettingsUrl={enterSettingsUrl}
      exitSettingsUrl={exitSettingsUrl}
      hideTeamSwitcher={hideTeamSwitcher}
      isHidden={hideNav}
      pageEntries={allPageEntries}
      settingSections={settingsItems}
      settingsRoot={settingsRoot}
      useLegacyPrimaryNavLayout={!newNavigationEnabled}
    />
  );
});
